import { Menu } from "antd";
import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { canAccess } from "../../../../../core/router/auth/permissions";
import { navigation } from "../../../../../core/router/config/navigation";
import { isMenuItemGroup } from "../../../../../core/router/types/menuTypes";
import { TextStyle } from "../../../../../core/styles/TextStyles";
import { CustomIcon } from "../../../../Icon/custom";
import { Link } from "../../../../Link";

const { SubMenu } = Menu;

/**
 * Properties of the Menu component
 * */
type MenuComponentProps = Readonly<{}>;

/**
 * MainMenu component is responsible for
 * building the menu from navigation.
 * Every item of menu should point to the corresponding path.
 * */
export const MainMenu: FC<MenuComponentProps> = () => {
    const [selectedKey, setSelectedKey] = useState(window.location.pathname);

    const { t } = useTranslation();

    const findDefaultOpenKeys = () => {
        const subMenuObjectArray = navigation
            .filter((menuItemGroup) => menuItemGroup.subMenus !== undefined)
            .map((menuItemGroup) => menuItemGroup.subMenus);

        for (const [key, value] of Object.entries(subMenuObjectArray)) {
            if (value !== undefined && key !== undefined) {
                const currentlyOpenedSubMenu = value.find((subMenuItem) => subMenuItem.menuItems.some((val) => val.to === selectedKey));
                if (currentlyOpenedSubMenu !== undefined) {
                    return [currentlyOpenedSubMenu.key];
                }
            }
        }
        return [];
    };

    return (
        <>
            <Menu
                defaultSelectedKeys={[selectedKey]}
                defaultOpenKeys={findDefaultOpenKeys()}
                mode="inline"
                onSelect={(key) => {
                    setSelectedKey(key.key);
                }}
            >
                {navigation.map((item) => {
                    if (isMenuItemGroup(item)) {
                        const filteredSubMenus = item.subMenus
                            ?.map((subMenu) => ({
                                ...subMenu,
                                menuItems: subMenu.menuItems.filter(
                                    (menuItem) => !menuItem?.permission || canAccess(menuItem?.permission, "canRead"),
                                ),
                            }))
                            .filter((subMenu) => subMenu.menuItems.length > 0);

                        const filteredUnclassifiedItems = item.unclassifiedItems?.filter(
                            (menuItem) => !menuItem?.permission || canAccess(menuItem?.permission, "canRead"),
                        );

                        if (
                            (!filteredSubMenus || filteredSubMenus.length === 0) &&
                            (!filteredUnclassifiedItems || filteredUnclassifiedItems.length === 0)
                        ) {
                            return null;
                        }

                        return (
                            <Menu.ItemGroup key={`${item.key}-submenu`} title={t(item.title)}>
                                {filteredSubMenus?.map(({ key, icon, title, menuItems }) => (
                                    <SubMenu
                                        key={key}
                                        icon={icon ? <CustomIcon iconSource={icon} /> : null}
                                        title={<span style={{ marginLeft: "10px" }}>{t(title)}</span>}
                                    >
                                        {menuItems.map(({ route, to, titleKey }) => (
                                            <Menu.Item key={to}>
                                                <Link route={route} to={to} className={item.key}>
                                                    {t(titleKey)}
                                                </Link>
                                            </Menu.Item>
                                        ))}
                                    </SubMenu>
                                ))}
                                {filteredUnclassifiedItems?.map(({ to, menuIcon, route, titleKey }) => (
                                    <Menu.Item key={to} icon={menuIcon ? <CustomIcon iconSource={menuIcon} /> : null}>
                                        <Link route={route} to={to} className={item.key}>
                                            <span style={{ marginLeft: "10px" }}>{t(titleKey)}</span>
                                        </Link>
                                    </Menu.Item>
                                ))}
                            </Menu.ItemGroup>
                        );
                    }
                    return null;
                })}
            </Menu>
            <TextStyle style={{ textAlign: "center" }}>APAdmin v{APP_INFO.VERSION}</TextStyle>
        </>
    );
};
